import React, { useEffect, useMemo, useState } from "react";
import { Wand2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Seg } from "./RigControls";

const NEED = [
  { value: "innkvartering", label: "Innkvartering" },
  { value: "kombinasjon", label: "Kombinasjon" },
  { value: "kontor", label: "Kontor" },
];
const INCLUDES = [
  { key: "gang", label: "Gang" },
  { key: "trapp", label: "Trapp" },
  { key: "teknisk", label: "Teknisk" },
  { key: "felles", label: "Fellesrom" },
  { key: "vaskeri", label: "Vaskeri" },
];

const Toggle = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} aria-pressed={on} data-testid={testId}
    className={cn("inline-flex h-6 items-center rounded-full px-2.5 text-[11.5px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", on ? "bg-foreground text-background" : "bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground line-through decoration-muted-foreground/60")}>
    {children}
  </button>
);

/**
 * "Generer riggforslag" as a short, command-like request: need → deterministic rig. The panel explains the basis and the
 * expected output before anything changes; the result is undoable (Ctrl+Z / toast action).
 */
export function RigGeneratePopover({ ed, children, open, onOpenChange, align = "end" }) {
  const { project, library, generate, generating, modules } = ed;
  const [persons, setPersons] = useState(project?.capacity_persons || 24);
  const [floors, setFloors] = useState(null);
  const [need, setNeed] = useState("innkvartering");
  const [inc, setInc] = useState({ gang: true, trapp: true, teknisk: true, felles: true, vaskeri: true });
  useEffect(() => { if (open) setPersons(project?.capacity_persons || 24); }, [open, project]);

  const unit = useMemo(() => {
    const items = library?.items || [];
    if (need === "kontor") return items.find((t) => t.code === "KT8") || items.find((t) => t.kind === "kontor");
    return items.find((t) => t.code === "B24") || items.find((t) => t.kind === "bolig");
  }, [library, need]);
  const p = Math.max(1, Number(persons) || 1);
  const cap = Math.max(1, unit?.capacity_persons || 1);
  const nUnits = Math.ceil(p / cap);
  const autoFloors = floors || (nUnits >= 16 ? 2 : 1);
  const perRow = Math.ceil(Math.ceil(nUnits / autoFloors) / 2);
  const extras = INCLUDES.filter((o) => inc[o.key]).length;

  const submit = (e) => {
    e?.preventDefault?.();
    generate({ persons: p, floors: floors || undefined, need_type: need, include: inc }, {
      onSuccess: (res) => {
        onOpenChange(false);
        const k = res?.summary?.kpi || {};
        toast.success("Riggforslag generert", {
          description: `${k.modules ?? "–"} moduler · ${k.beds ?? "–"} senger · ${res?.design?.floors ?? autoFloors} etasje${(res?.design?.floors ?? autoFloors) > 1 ? "r" : ""} · lagret som v${res?.design?.version ?? ""}`,
          action: { label: "Angre", onClick: () => ed.undo() },
          duration: 6000,
        });
      },
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} sideOffset={8} className="w-[400px] p-0 shadow-pop" data-testid="rig-generate-dialog">
        <form onSubmit={submit}>
          <div className="px-4 pb-3 pt-3.5">
            <div className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
              <div className="text-[13px] font-medium">Generer riggforslag</div>
            </div>
            <p className="mt-1 text-[12px] leading-4 text-muted-foreground">Regelbasert og deterministisk – samme behov gir alltid samme rigg. Ingen AI. Forslaget kan redigeres fritt etterpå.</p>

            <div className="mt-3.5 flex flex-wrap items-center gap-x-2 gap-y-2 text-[12.5px]">
              <span className="text-muted-foreground">Rigg for</span>
              <Input type="number" min={1} max={2000} value={persons} onChange={(e) => setPersons(e.target.value)} className="h-7 w-[4.5rem] font-mono tabular" data-testid="rig-generate-persons" autoFocus aria-label="Antall personer" />
              <span className="text-muted-foreground">personer ·</span>
              <Seg size="sm" className="bg-muted/60" value={need} options={NEED} onChange={setNeed} testId="rig-generate-need" />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 text-[12.5px]">
              <span className="text-muted-foreground">Etasjer</span>
              <Seg size="sm" className="bg-muted/60" value={floors} options={[{ value: null, label: "Auto" }, { value: 1, label: "1" }, { value: 2, label: "2" }, { value: 3, label: "3" }]} onChange={setFloors} testId="rig-generate-floors" />
              <span className="ml-auto text-muted-foreground">Inkluder</span>
              <div className="flex flex-wrap gap-1">
                {INCLUDES.map((o) => <Toggle key={o.key} on={!!inc[o.key]} onClick={() => setInc((s) => ({ ...s, [o.key]: !s[o.key] }))} testId={`rig-generate-include-${o.key}`}>{o.label}</Toggle>)}
              </div>
            </div>
          </div>

          <div className="border-t border-border/60 bg-muted/40 px-4 py-3 text-[12px] leading-5" data-testid="rig-generate-preview">
            {unit ? (
              <>
                <div className="text-[11px] font-medium text-muted-foreground">Forventet resultat</div>
                <div className="mt-0.5"><span className="font-medium tabular">{nUnits} × {unit.code}</span> <span className="text-muted-foreground">({unit.name}, {cap} pers/modul)</span> → <span className="tabular">{nUnits * cap} plasser</span> i <span className="tabular">{autoFloors} etasje{autoFloors > 1 ? "r" : ""}</span>, <span className="tabular">{perRow}</span> per rekke{extras ? <>, + {extras} typer støttemoduler</> : null}.</div>
                <div className="mt-1 text-muted-foreground">Rekker rygg mot rygg med felles gangsone · inngang og trapp i endene · teknikk mot nord · fellesfunksjoner mot sør.</div>
                {modules.length > 0 && <div className="mt-1.5 flex items-center gap-1.5 text-foreground/80"><Undo2 className="h-3.5 w-3.5 text-muted-foreground" /> Erstatter dagens design ({modules.length} moduler). Angres med <kbd className="kbd">Ctrl</kbd>+<kbd className="kbd">Z</kbd>.</div>}
              </>
            ) : (
              <span className="text-muted-foreground">Ingen aktiv modultype for dette behovet.</span>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-4 py-3">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="rig-generate-cancel">Avbryt</Button>
            <Button type="submit" size="sm" disabled={generating || !unit} data-testid="rig-generate-submit"><Wand2 /> {generating ? "Genererer…" : "Generer rigg"}</Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** Backwards-compatible name. */
export const RigGenerateDialog = RigGeneratePopover;
